import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Menu,
  MenuItem as MuiMenuItem,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  SimCard,
  Assessment,
  AccountBalance,
  Notifications,
  Settings,
  Logout,
  Person,
  Group,
  Add,
  RequestPage,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import Badge from '@mui/material/Badge';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import BusinessIcon from '@mui/icons-material/Business';

const drawerWidth = 240;

export function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { signOut, user, profile } = useAuth();
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [logoError, setLogoError] = useState(false);

  // Handle logo load error
  const handleLogoError = () => {
    setLogoError(true);
  };

  // Fetch unread notification count
  const fetchUnreadCount = useCallback(async () => {
    if (!profile?.organization_id) {
      setUnreadCount(0);
      return;
    }
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', profile.organization_id)
        .is('user_id', null) 
        .eq('is_read', false); 

      if (error) {
        console.error('Error fetching unread count:', error);
        setUnreadCount(0); 
      } else {
        setUnreadCount(count ?? 0);
      }
    } catch (err) {
      console.error('Unexpected error fetching unread count:', err);
      setUnreadCount(0);
    }
  }, [profile?.organization_id]); // Depend on organization_id

  // Effect for initial fetch
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Effect for Realtime subscription
  useEffect(() => {
    if (!profile?.organization_id) return; // Don't subscribe without org ID

    const channel = supabase.channel('public:notifications:org_level')
      .on(
        'postgres_changes',
        { 
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'notifications',
          filter: `organization_id=eq.${profile.organization_id}` // Filter for user's org
          // We check user_id === null and is_read === false in the handler if needed,
          // but re-fetching the count is safer on any relevant org change.
        },
        (payload) => {
          console.log('Realtime notification change received:', payload);
          // Re-fetch the count whenever a relevant change occurs
          fetchUnreadCount(); 
        }
      )
      .subscribe();

    console.log('Subscribed to notifications channel for org:', profile.organization_id);

    // Cleanup function to unsubscribe
    return () => {
      console.log('Unsubscribing from notifications channel');
      supabase.removeChannel(channel);
    };

  }, [profile?.organization_id, fetchUnreadCount]); // Depend on org ID and the fetch function reference

  // Fetch pending requests count and subscribe to changes (only for superadmin)
  useEffect(() => {
    if (profile?.role !== 'superadmin') {
      setPendingRequestCount(0); // Ensure count is 0 if not superadmin
      return; // Don't run fetch/subscription if not superadmin
    }

    let channel: ReturnType<typeof supabase.channel> | null = null;

    // Function to fetch the current count
    const fetchPendingCount = async () => {
      try {
        const { count, error } = await supabase
          .from('admin_requests')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending');
        
        if (error) {
          console.error('Error fetching pending request count:', error);
          setPendingRequestCount(0);
        } else {
          setPendingRequestCount(count ?? 0);
        }
      } catch (err) {
        console.error('Error in fetchPendingCount:', err);
        setPendingRequestCount(0);
      }
    };

    // Fetch initial count
    fetchPendingCount();

    // Set up realtime subscription
    channel = supabase
      .channel('public:admin_requests')
      .on(
        'postgres_changes',
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'admin_requests',
          filter: 'status=eq.pending' // Only trigger on new PENDING requests
        },
        (payload) => {
          console.log('New pending request detected, fetching count...', payload);
          fetchPendingCount(); // Re-fetch count on new insert
        }
      )
      .subscribe((status, err) => {
          if (status === 'SUBSCRIBED') {
              console.log('Subscribed to admin_requests inserts');
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
              console.error(`Subscription error: ${status}`, err);
          }
      });

    // Cleanup function
    return () => {
      if (channel) {
        supabase.removeChannel(channel)
            .then(() => console.log('Unsubscribed from admin_requests'))
            .catch(err => console.error('Error unsubscribing:', err));
      }
    };
  }, [profile?.role]); // Re-run effect if role changes

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  // Consolidate ALL navigation items here
  const navItems = [
    // Regular User Items
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard', roles: ['user', 'admin', 'superadmin'] },
    { text: 'SIM Management', icon: <SimCard />, path: '/sims', roles: ['user', 'admin', 'superadmin'] },
    { text: 'Reports', icon: <Assessment />, path: '/reports', roles: ['user', 'admin', 'superadmin'] },
    { text: 'Wallet', icon: <AccountBalance />, path: '/wallet', roles: ['user', 'admin', 'superadmin'] },
    
    // Admin + SuperAdmin Items
    { text: 'Request SIMs', icon: <Add />, path: '/request-sims', roles: ['admin', 'superadmin'] },
    { text: 'Clusters', icon: <Group />, path: '/clusters', roles: ['admin', 'superadmin'] },
    { 
      text: 'Notifications', 
      icon: <Badge badgeContent={unreadCount} color="error" invisible={unreadCount === 0}><Notifications /></Badge>, 
      path: '/notifications', 
      roles: ['admin', 'superadmin'] 
    },
    { text: 'Submit Request', icon: <PlaylistAddIcon />, path: '/submit-request', roles: ['admin', 'superadmin'] },
    { 
      text: 'Manage Requests', 
      icon: <Badge badgeContent={profile?.role === 'superadmin' ? pendingRequestCount : 0} color="error" invisible={pendingRequestCount === 0 || profile?.role !== 'superadmin'}><RequestPage /></Badge>, 
      path: '/admin/requests', 
      roles: ['superadmin']
    },

    // SuperAdmin Only Items
    { text: 'Organizations', icon: <BusinessIcon />, path: '/admin/organizations', roles: ['superadmin'] },
    { text: 'New Organization', icon: <Add />, path: '/admin/organizations/new', roles: ['superadmin'] },
    { text: 'Adjust Wallet (Fulfillment)', icon: <AccountBalance />, path: '/admin/adjust-wallet', roles: ['superadmin'] },
    { text: 'Record Payment', icon: <PlaylistAddIcon />, path: '/admin/record-payment', roles: ['superadmin'] },
    // Add User Management link here when created: { text: 'Users', icon: <PeopleIcon />, path: '/admin/users', roles: ['superadmin'] } 
  ];

  const userRole = profile?.role || 'user';

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '64px' // Ensure consistent height
      }}>
        {!logoError ? (
          <img
            src="/logo.svg" // You can change this path based on where you place your logo
            alt="iotsims.co.za"
            style={{
              maxWidth: '180px',
              maxHeight: '40px',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain'
            }}
            onError={handleLogoError}
          />
        ) : (
          // Fallback to text if logo fails to load
          <Typography variant="h6" component="div" sx={{ fontSize: '1.1rem' }}>
            iotsims.co.za
          </Typography>
        )}
      </Box>
      <Divider />
      <List sx={{ flexGrow: 1 }}>
        {/* Render all items from the single navItems array */}
        {navItems.map((item) => (
          item.roles.includes(userRole) && (
            <ListItem key={item.text} disablePadding>
              <ListItemButton 
                component={Link} 
                to={item.path}
                selected={isActive(item.path)}
                sx={{
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                    '& .MuiListItemIcon-root': { 
                      color: 'secondary.main' 
                    },
                    '& .MuiListItemText-primary': {
                      color: 'secondary.main'
                    }
                  }, 
                  ...(isActive(item.path) && {
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                      color: 'secondary.main', 
                      fontWeight: 'medium',
                      '& .MuiListItemIcon-root': { 
                        color: 'secondary.main' 
                      }
                    }
                  })
                }}
              >
                {/* Ensure icon is always rendered */}
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          )
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 2, mt: 'auto' }}>
        {/* User info display */}
        <Typography variant="caption">Logged in as:</Typography>
        <Typography variant="body2">{user?.email}</Typography>
        {profile?.role && (
          <Chip
            label={profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
            size="small"
            variant="filled"
            color='primary'
            sx={{ mt: 1, mb: 1, width: '100%' }}
          />
        )}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<Logout />}
          onClick={handleLogout}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
      }}
    >
      {drawerContent}
    </Drawer>
  );
} 